import { useState, useCallback, useMemo, useEffect } from 'react';
import { GridSchema, GridColumnState } from './grid.types';
import {
  useGetTableSchemaQuery,
  useUpdateColumnPreferencesMutation,
  useGetColumnPreferencesQuery,
  useResetColumnPreferencesMutation,
  useGetViewsQuery,
  useCloneViewMutation,
  useDeleteViewMutation,
  ColumnPreferences,
  ViewInfo
} from '@/store/api/tableConfigApi';
import toast from '@/utils/toast';

const toColumnStates = (schema: GridSchema): GridColumnState[] =>
  [...schema.columns]
    .sort((a, b) => a.order - b.order)
    .map(({ key, label, visible, order, width }) => ({
      key,
      label,
      visible,
      order,
      width,
    }));

interface UseGridCustomizationWithAPIOptions {
  entity?: string;
  fallbackSchema?: GridSchema;
  enableAPIConfig?: boolean;
  currentView?: string;
}

const useGridCustomizationWithAPI = ({
  entity,
  fallbackSchema,
  enableAPIConfig = true,
  currentView: initialView
}: UseGridCustomizationWithAPIOptions) => {
  const [currentView, setCurrentView] = useState<string | undefined>(initialView);

  // Memoize query parameters to prevent unnecessary re-fetches
  const schemaQueryParams = useMemo(() => ({
    entity: entity || '',
    view: currentView
  }), [entity, currentView]);

  const preferencesQueryParams = useMemo(() => ({
    entity: entity || '',
    view: currentView
  }), [entity, currentView]);

  const {
    data: apiSchema,
    isLoading: schemaLoading,
    error: schemaError,
  } = useGetTableSchemaQuery(schemaQueryParams, {
    skip: !enableAPIConfig || !entity,
  });

  // Column preferences API hooks
  const {
    data: columnPreferences,
    isLoading: preferencesLoading,
    error: preferencesError
  } = useGetColumnPreferencesQuery(preferencesQueryParams, {
    skip: !enableAPIConfig || !entity
  });

  // Views API hooks
  const {
    data: views = [],
    isLoading: viewsLoading,
    error: viewsError
  } = useGetViewsQuery(entity || '', {
    skip: !enableAPIConfig || !entity
  });

  const [updatePreferences, {
    isLoading: isUpdatingPreferences,
    error: updatePreferencesError
  }] = useUpdateColumnPreferencesMutation();

  const [resetPreferences, {
    isLoading: isResettingPreferences,
    error: resetPreferencesError
  }] = useResetColumnPreferencesMutation();

  const [cloneView, {
    isLoading: isCloningView,
    error: cloneViewError
  }] = useCloneViewMutation();

  const [deleteView, {
    isLoading: isDeletingView,
    error: deleteViewError
  }] = useDeleteViewMutation();

  // Use API schema if available, otherwise fallback to provided schema
  const activeSchema = apiSchema || fallbackSchema;

  const [columns, setColumns] = useState<GridColumnState[]>(() =>
    activeSchema ? toColumnStates(activeSchema) : []
  );

  // Update columns when schema changes
  useEffect(() => {
    if (activeSchema) {
      setColumns(toColumnStates(activeSchema));
    }
  }, [activeSchema]);

  // Apply loaded preferences to columns
  useEffect(() => {
    if (columnPreferences && activeSchema) {
      setColumns(prev => {
        const updatedColumns = prev.map(col => {
          const pref = columnPreferences[col.key];
          if (pref) {
            return {
              ...col,
              visible: pref.visible !== undefined ? pref.visible : col.visible,
              order: pref.order !== undefined ? pref.order : col.order,
              width: pref.width !== undefined ? pref.width : col.width,
            };
          }
          return col;
        });
        
        // Sort by order to ensure correct column positioning
        return updatedColumns.sort((a, b) => a.order - b.order);
      });
    }
  }, [columnPreferences, activeSchema]);

  const setVisibility = useCallback((key: string, visible: boolean) => {
    setColumns((prev) =>
      prev.map((col) => (col.key === key ? { ...col, visible } : col))
    );
  }, []);

  const setWidth = useCallback((key: string, width: number) => {
    setColumns((prev) =>
      prev.map((col) => (col.key === key ? { ...col, width } : col))
    );
  }, []);

  // Move a column up or down by one position
  const moveColumn = useCallback((key: string, direction: 'up' | 'down') => {
    setColumns((prev) => {
      const idx = prev.findIndex((col) => col.key === key);
      if (idx === -1) return prev;
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= prev.length) return prev;

      const next = [...prev];
      [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];

      // Re-assign order values to match new positions
      return next.map((col, i) => ({ ...col, order: (i + 1) * 10 }));
    });
  }, []);

  // Set specific column order
  const setColumnOrder = useCallback((columnOrders: { key: string; order: number }[]) => {
    setColumns((prev) => {
      const updatedColumns = prev.map(col => {
        const orderUpdate = columnOrders.find(co => co.key === col.key);
        return orderUpdate ? { ...col, order: orderUpdate.order } : col;
      }).sort((a, b) => a.order - b.order);
      
      return updatedColumns;
    });
  }, []);

  // Reorder columns by dragging (for drag-and-drop functionality)
  const reorderColumns = useCallback((newColumnOrder: string[]) => {
    setColumns((prev) => {
      const columnMap = new Map(prev.map(col => [col.key, col]));
      const updatedColumns = newColumnOrder
        .map(key => columnMap.get(key))
        .filter(Boolean)
        .map((col, index) => ({ ...col!, order: (index + 1) * 10 }));
      
      return updatedColumns;
    });
  }, []);

  const resetToSchema = useCallback(() => {
    if (activeSchema) {
      setColumns(toColumnStates(activeSchema));
    }
  }, [activeSchema]);

  // Save all column preferences to API
  const savePreferencesToAPI = useCallback(async () => {
    if (enableAPIConfig && entity) {
      try {
        const preferences: ColumnPreferences = {};
        columns.forEach(col => {
          preferences[col.key] = {
            visible: col.visible,
            order: col.order,
            width: col.width
          };
        });
        await updatePreferences({ entity, preferences, view: currentView }).unwrap();
        toast.success('Column preferences saved successfully');
      } catch (error) {
        console.error('Failed to save preferences to API:', error);
        throw error;
      }
    }
  }, [enableAPIConfig, entity, columns, updatePreferences, currentView]);

  // Reset all column preferences to defaults via API
  const resetPreferencesToAPI = useCallback(async () => {
    if (enableAPIConfig && entity) {
      try {
        await resetPreferences({ entity, view: currentView });
        // After successful reset, update local state to schema defaults
        if (activeSchema) {
          setColumns(toColumnStates(activeSchema));
        }
        toast.success('Column preferences reset to default');
      } catch (error) {
        console.error('Failed to reset preferences via API:', error);
        throw error;
      }
    }
  }, [enableAPIConfig, entity, resetPreferences, activeSchema, currentView]);

  const switchView = useCallback((viewName?: string) => {
    setCurrentView(viewName);
  }, []);

  const createView = useCallback(async (viewName: string) => {
    if (enableAPIConfig && entity) {
      try {
        await cloneView({
          entity,
          source_view: currentView,
          target_view: viewName
        }).unwrap();
        setCurrentView(viewName);
        toast.success(`View "${viewName}" created successfully`);
      } catch (error) {
        console.error('Failed to create view:', error);
        toast.error('Failed to create view');
        throw error;
      }
    }
  }, [enableAPIConfig, entity, cloneView, currentView]);

  const removeView = useCallback(async (viewName: string) => {
    if (enableAPIConfig && entity) {
      try {
        await deleteView({ entity, viewName }).unwrap();
        if (currentView === viewName) {
          setCurrentView(undefined);
        }
        toast.success(`View "${viewName}" deleted successfully`);
      } catch (error) {
        console.error('Failed to delete view:', error);
        toast.error('Failed to delete view');
        throw error;
      }
    }
  }, [enableAPIConfig, entity, deleteView, currentView]);

  const visibleColumns = useMemo(
    () => columns.filter((col) => col.visible),
    [columns]
  );

  return {
    columns,
    visibleColumns,
    setVisibility,
    setWidth,
    moveColumn,
    setColumnOrder,
    reorderColumns,
    resetToSchema,
    savePreferencesToAPI,
    resetPreferencesToAPI,
    // View management
    views,
    currentView,
    switchView,
    createView,
    removeView,
    viewsLoading,
    viewsError,
    isCloningView,
    isDeletingView,
    // API-related states
    schemaLoading,
    schemaError,
    hasAPISchema: !!apiSchema,
    activeSchema,
    // Preferences-related states
    columnPreferences,
    preferencesLoading,
    preferencesError,
    isUpdatingPreferences,
    updatePreferencesError,
    isResettingPreferences,
    resetPreferencesError,
  };
};

export default useGridCustomizationWithAPI;